<template>
  <div class="quiz-container">
    <BattleHeader title="墨客争霸" @back="goBack" />

    <!-- 主要内容区 -->
    <main class="quiz-main">
      <!-- 答题区域 -->
      <div class="quiz-section" id="quiz-section">
        <!-- 题目进度 -->
        <div class="progress-bar">
          <div class="progress-info">
            已完成 <span class="progress-number">{{ answerRecords.length }}</span> 题，答对 <span class="progress-number">{{ Math.floor(score / 10) }}</span> 题
          </div>
          <div class="timer">
            <i class="far fa-clock timer-icon">
            </i>
            <span id="countdown-timer">
              {{ formattedTime }}
            </span>
          </div>
        </div>

        <!-- 题目内容 -->
        <div class="question-card" v-if="currentQuestionData">
          <h2 class="question-text">
            {{ currentQuestionData.question }}
          </h2>
          
          <!-- 选项列表 -->
          <div class="options-container">
            <div 
              v-for="(option, index) in currentQuestionData.options" 
              :key="index"
              class="option-item"
              :class="{
                'selected': selectedOption === option.key,
                'correct': answered && option.key === correctAnswer,
                'incorrect': answered && selectedOption === option.key && option.key !== correctAnswer
              }"
              @click="selectOption(option.key)"
            >
              <div class="option-key">
                {{ option.key }}
              </div>
              <div class="option-text">
                {{ option.text }}
              </div>
            </div>
          </div>
        </div>
        <div class="question-card" v-else>
          <h2 class="question-text">加载题目中...</h2>
        </div>
      </div>

      <!-- 导航按钮区 -->
      <div class="nav-buttons">
        <button 
          class="nav-btn nav-btn-next"
          @click="handleNext"
        >
          下一题
          <i class="fas fa-arrow-right">
          </i>
        </button>
      </div>


    </main>

    <!-- 底部链接 -->
    <footer class="quiz-footer">
      <div class="footer-content">
        <a href="javascript:void(0)" class="fas fa-home back-link" @click="showBackHomeConfirm">
          回到主页
        </a>
      </div>
    </footer>

    <!-- 确认弹窗 -->

  </div>
</template>

<script>
import { getOptions, getOptionConf, finishQuiz as finishQuizApi } from '@/api/api'
import { globalAuthState, initAuthState } from '@/utils/auth'
import { confirm, alert } from '@/utils/modal'
import BattleHeader from '@/components/BattleHeader.vue'

export default {
  name: 'QuizPage',
  components: { BattleHeader },
  async beforeCreate() {
    // 初始化登录状态
    await initAuthState();
    // 检查是否有用户信息
    if (!globalAuthState.isLogin) {
      // 没有用户信息，跳转到登录页面
      this.$router.push('/login')
    }
  },
  data() {
    return {
      currentQuestion: 1,
      selectedOption: null,
      answered: false,
      timeRemaining: 0, // 总倒计时
      selectedTime: 0, // 总挑战时间
      currentStage: 0, // 当前阶段：0-初级，1-中级，2-高级
      totalTime: 0, // 总倒计时时间（秒）
      usedTime: 0, // 已使用时间（秒）
      stageLimits: [0, 0, 0], // 各阶段时间限制（秒）
      timer: null,
      questionStartRemaining: 0,
      answerRecords: [],
      currentQuestionData: null, // 当前题目数据

    }
  },
  computed: {
    correctAnswer() {
      // 如果correctAnswer是数字索引，则返回对应选项的key；否则直接返回
      const answer = this.currentQuestionData?.correctAnswer;
      if (typeof answer === 'number' || (typeof answer === 'string' && /^\d+$/.test(answer))) {
        const index = parseInt(answer);
        const options = this.currentQuestionData?.options;
        if (index >= 0 && options && index < options.length) {
          return options[index].key;
        }
      }
      return answer;
    },
    formattedTime() {
      const minutes = Math.floor(this.timeRemaining / 60);
      const seconds = this.timeRemaining % 60;
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    },

    score() {
      // 基于answerRecords动态计算总分
      return this.answerRecords.reduce((total, record) => {
        return total + record.score;
      }, 0);
    },
  },
  mounted() {
    // 首先调用getOptionConf获取配置
    this.getOptionConf();
  },
  beforeUnmount() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  },
  methods: {
    // 获取配置信息
    getOptionConf() {
      const selectedTime = parseInt(this.$route.query.time) || 5;
      getOptionConf({selectedTime: selectedTime}).then(res => {
        console.log('Option Conf Response:', res);
        
        if (res && res.data) {
          // 设置各阶段时间限制（秒）
          this.stageLimits[0] = (res.data.limit0 || 0) * 60;
          this.stageLimits[1] = this.stageLimits[0] + (res.data.limit1 || 0) * 60;
          this.stageLimits[2] = this.stageLimits[1] + (res.data.limit2 || 0) * 60;
          
          // 计算总挑战时间（分钟）
          this.selectedTime = (res.data.limit0 || 0) + (res.data.limit1 || 0) + (res.data.limit2 || 0);
          
          // 计算总倒计时时间（秒）
          this.totalTime = this.selectedTime * 60;
          this.timeRemaining = this.totalTime;
          
          // 初始化已使用时间
          this.usedTime = 0;
          
          // 初始化当前阶段
          this.updateCurrentStage();
          
          // 获取第一题
          this.getOptions();
          
          // 开始倒计时
          this.startTimer();
          this.questionStartRemaining = this.timeRemaining;
        }
      }).catch(error => {
        console.error('Failed to fetch option config:', error);
      });
    },
    // 更新当前阶段
    updateCurrentStage() {
      if (this.usedTime <= this.stageLimits[0]) {
        this.currentStage = 0;
      } else if (this.usedTime <= this.stageLimits[1]) {
        this.currentStage = 1;
      } else if (this.usedTime <= this.stageLimits[2]) {
        this.currentStage = 2;
      }
    },
    getOptions() {
      // 传递selectedTime和当前阶段参数
      getOptions({selectedTime: this.selectedTime, level: this.currentStage}).then(res => {
        console.log('API Response:', res); // 添加日志查看实际返回的数据结构
        
        // 检查返回的数据结构
        if (!res || !res.data) {
          console.error('Invalid API response structure:', res);
          return;
        }
        
        // 处理返回的数据，假设返回的是单个题目
        const item = res.data;
        this.currentQuestionData = {
          question: item.title,
          options: JSON.parse(item.content).map(opt => ({
            key: opt.label,
            text: opt.text
          })),
          correctAnswer: item.answer,
          optionId: item.option_id || item.id || null,
          optionType: item.optionType || item.type || 'single',
          answerRaw: item.answer,
          explanation: ""
        };
      }).catch(error => {
        console.error('Failed to fetch questions:', error);
        // 出错时设置默认值
        this.currentQuestionData = { 
          question: '', 
          options: [], 
          correctAnswer: null, 
          answerRaw: null, 
          optionId: null, 
          optionType: 'single'
        };
      });
    },
    startTimer() {
      this.timer = setInterval(() => {
        if (this.timeRemaining > 0) {
          this.timeRemaining--;
          this.usedTime++;
          this.updateCurrentStage();
        } else {
          this.timeUp();
        }
      }, 1000);
    },
    timeUp() {
      // 检查当前是否有选中的选项，如果有则检查答案
      if (this.selectedOption) {
        this.checkAnswer(true);
      }
      
      // 总倒计时结束，立即提交答题结果
      this.finishQuiz();
    },
    goBack() {
      this.$router.push('/')
    },
    selectOption(optionKey) {
      if (this.answered) return;
      
      this.selectedOption = optionKey;
    },
    
    handleNext() {
      // 如果当前问题没有回答，不允许点击下一题
      if (!this.selectedOption) {
        return;
      }
      
      // 检查答案
      this.checkAnswer(true);
      
      // 只要倒计时没到，就继续获取下一题
      if (this.timeRemaining > 0) {
        this.nextQuestion();
      } else {
        this.finishQuiz();
      }
    },
    checkAnswer(silent = false) {
      if (!this.selectedOption || !this.currentQuestionData) return false;
      
      // 如果不是静默模式，设置answered为true以显示答案
      if (!silent) {
        this.answered = true;
      }
      
      const q = this.currentQuestionData;
      const replyIndex = q.options.findIndex(opt => opt.key === this.selectedOption);
      const answerIndex = typeof q.answerRaw !== 'undefined' && q.answerRaw !== null
        ? q.answerRaw
        : q.options.findIndex(opt => opt.key === this.correctAnswer);
      const record = {
        title: q.question,
        sort: this.currentQuestion,
        optionId: q.optionId || null,
        optionType: q.optionType || 'single',
        options: q.options.map(opt => ({ label: opt.key, text: opt.text })),
        answer: String(answerIndex),
        reply: String(replyIndex),
        validity: this.selectedOption === this.correctAnswer ? 1 : 0,
        score: this.selectedOption === this.correctAnswer ? 10 : 0,
        useTime: Math.max(0, this.questionStartRemaining - this.timeRemaining)
      };
      const existingIdx = this.answerRecords.findIndex(r => r.sort === this.currentQuestion);
      if (existingIdx >= 0) {
        this.answerRecords.splice(existingIdx, 1, record);
      } else {
        this.answerRecords.push(record);
      }
      
      return true;
    },

    nextQuestion() {
      // 增加题目计数
      this.currentQuestion++;
      
      // 重置状态
      this.answered = false;
      this.selectedOption = null;
      this.questionStartRemaining = this.timeRemaining;
      
      // 获取下一题
      this.getOptions();
    },

    async finishQuiz() {
      clearInterval(this.timer);
      // 提交答题结果
      const totalQuestions = this.answerRecords.length;
      const correctAnswers = Math.floor(this.score / 10);
      finishQuizApi({
        score: this.score,
        correct: correctAnswers,
        total: totalQuestions,
        content: JSON.stringify(this.answerRecords),
        selectedTime: this.selectedTime,
      }).then(async res => {
        if (res.code === 1) {
          const recordId = res.data.lastId;
          // 跳转到结果页面
          this.$router.push({
            name: 'Result',
              query: {
                score: this.score,
                correct: correctAnswers,
                wrong: totalQuestions - correctAnswers,
                total: totalQuestions,
                timeLimit: this.selectedTime,
                recordId: recordId,
                ranking: res.data.rankStr
              }
          });
        } else {
          await alert('提交答题结果失败！');
        }
      })
    },
    // 显示返回主页确认弹窗
    async showBackHomeConfirm() {
      const confirmed = await confirm({
        title: '温馨提示',
        message: '跳转后，已答题数据不会保存，请确认是否跳转？',
        confirmText: '确定',
        cancelText: '取消'
      });
      
      if (confirmed) {
        this.$router.push('/');
      }
    }
  }
}
</script>

<style scoped>
.quiz-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #F5F5F5;
  font-family: 'PingFang SC', 'Helvetica Neue', Arial, sans-serif;
}

.quiz-main {
  flex: 1;
  padding: calc(var(--battle-header-height) + 20px) 24px 24px;
  max-width: 672px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.quiz-section {
  margin-bottom: 32px;
}

.progress-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.progress-info {
  color: #666666;
  font-size: 14px;
}

.progress-number {
  color: #D54D24;
}

.timer {
  background-color: #D54D24;
  color: white;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.timer-icon {
  margin-right: 6px;
  color: white;
}

.question-card {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 24px;
  margin-bottom: 32px;
}

.question-text {
  font-size: 20px;
  font-weight: bold;
  color: #333333;
  margin-bottom: 24px;
}

.options-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.option-item {
  padding: 16px;
  border: 2px solid #E5E5E5;
  border-radius: 8px;
  display: flex;
  align-items: flex-start;
  cursor: pointer;
  transition: all 0.3s ease;
}

.option-item:hover {
  background-color: #F5F5F5;
}

.option-item.selected {
  border-color: #D54D24;
  background-color: #FFF5F0;
}

.option-item.correct {
  border-color: #10B981;
  background-color: #F0FDF4;
}

.option-item.incorrect {
  border-color: #EF4444;
  background-color: #FEF2F2;
}

.option-key {
  background-color: #D54D24;
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
  font-weight: bold;
}

.option-text {
  color: #333333;
  flex: 1;
}

.explanation-card {
  background-color: #F5F5F5;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 32px;
}

.explanation-title {
  font-size: 18px;
  font-weight: bold;
  color: #333333;
  margin-bottom: 12px;
}

.explanation-text {
  color: #666666;
  line-height: 1.6;
}

.nav-buttons {
  display: flex;
  justify-content: center;
  gap: 16px;
}

.nav-btn {
  padding: 12px 48px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 300px;
  justify-content: center;
}

.nav-btn-next {
  background-color: #D54D24;
  color: white;
}

.nav-btn-next:hover {
  background-color: #C2410C;
}

.nav-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.quiz-footer {
  padding: 16px 24px;
  border-top: 1px solid #E5E5E5;
}

.footer-content {
  text-align: center;
}

.back-link {
  color: #3B82F6;
  text-decoration: none;
  font-size: 14px;
}

.back-link:hover {
  text-decoration: underline;
}

/* 确认弹窗样式 */
.confirm-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}

.confirm-modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-width: 400px;
  margin: 0 20px;
}

.confirm-modal-body {
  padding: 24px;
}

.confirm-modal-title {
  font-size: 20px;
  font-weight: bold;
  color: #333333;
  text-align: center;
  margin: 0 0 16px 0;
}

.confirm-modal-message {
  color: #999999;
  text-align: center;
  margin: 0 0 24px 0;
  font-size: 14px;
  line-height: 1.5;
}

.confirm-modal-buttons {
  display: flex;
  justify-content: center;
  gap: 16px;
}

.confirm-modal-btn {
  padding: 8px 24px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.confirm-modal-btn.cancel {
  background: #999999;
  color: white;
}

.confirm-modal-btn.cancel:hover {
  background: rgba(153, 153, 153, 0.8);
}

.confirm-modal-btn.confirm {
  background: #D54D24;
  color: white;
}

.confirm-modal-btn.confirm:hover {
  background: rgba(213, 77, 36, 0.9);
}
</style>
